import React, { useRef, useState, type JSX } from 'react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment-timezone';
import { Box, Button, Divider, FormControl, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';

import { WidgetFooter } from 'pages/WidgetManagement/common/WidgetFooter';
import { WidgetHeader } from 'pages/WidgetManagement/common/WidgetHeader';
import { MultiColumnList } from 'components/MultiColumnList';

import Download from '@mui/icons-material/Download';
import { useIsMount, useRBAC } from 'hooks';
import { getTimeInFromTo, getUniqueDeviceTypeIds } from '@fiji/common/src/utils/helpers';
import { useTransformData } from 'hooks/useTransformData';
import { useTypedSelector } from '@fiji/common';
import { useDispatch } from 'react-redux';
import { CustomTransComponent } from 'components';
import { setLoadGroupTrendsLegendData } from '@fiji/common/src/features/widgetManagement/loadGroupTrendsSlice';
import {
    useLazyGetLoadTrendConfigurationQuery,
    useLazyGetLoadTrendUnitsQuery,
} from '@fiji/common/src/features/deviceManagement/ontologyApi';
import { ArrowDropDown } from '@mui/icons-material';
import { loadColors } from 'pages/MultiLoadTrends/MultiLoadTrends';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export type TelemetryChartProps = {
    type?: 'area' | 'line';
    widgetTypeId?: string;
    route?: string;
    state?: string;
    maxCount: number;
    secondary: any[];
    widgetName: string;
    [key: string]: any;
};

const Component = React.forwardRef(
    (
        {
            widgetData,
            mode,
            payloadChangeHandler,
            downloadHandler,
            handlerLegendsPayload,
        }: {
            widgetData: TelemetryChartProps;
            mode?: string;

            payloadChangeHandler: any;
            downloadHandler: any;
            handlerLegendsPayload: any;
        },
        ref
    ): any => {
        const dispatch = useDispatch();
        const rolePermissions = useTypedSelector(selectCurrentPermission);
        const { hasPermission } = useRBAC(rolePermissions);
        const canViewLoadsGroupsTrends = hasPermission('load-telemetry_view-devices');
        const canViewLoadsGroupsTrendsExpired = hasPermission('load-telemetry_view-devices_hasExpired');
        const canDownloadLoadsGroupsTrends = hasPermission('load-telemetry_create-devices');
        const canDownloadLoadsGroupsTrendsExpired = hasPermission('load-telemetry_create-devices_hasExpired');
        const trendsLegendData: any = useTypedSelector(
            (state) => state.load_group_trends.trendsLegendData[widgetData?.widgetName]
        );
        const [getLoadTrendConfiguration, { data: configResponse }] = useLazyGetLoadTrendConfigurationQuery();
        const selectedGroup =
            trendsLegendData?.legendData?.find((item: any) => item?.id === trendsLegendData?.selectedId) ??
            widgetData?.secondary?.[0];
        const selectedModelCategory = selectedGroup?.modelCategory;

        const getDeviceType = (loadConfig?: any): any[] => {
            if (loadConfig) {
                return getUniqueDeviceTypeIds(loadConfig);
            }
            if (selectedGroup) {
                return getUniqueDeviceTypeIds(selectedGroup?.loadConfig);
            }
            return getUniqueDeviceTypeIds(widgetData?.secondary?.[0]?.loadConfig);
        };
        const [getLoadTrendUnits, { data: unitsResponse, isLoading, isFetching }] = useLazyGetLoadTrendUnitsQuery();
        const [unit, setUnit] = React.useState('');

        const { pointConversion, timezone, timezoneOffset } = useTransformData();
        const componentRef: any = useRef(null);
        const [chartHeight, setChartHeight] = useState<any>(null);
        const isFirstRender = useRef(true);

        const isMount = useIsMount();

        const chartRef: any = useRef(null);

        const [channelIds, setChannelIds] = useState<any>([]);
        const [selectedLoads, setSelectedLoads] = useState<any>([]);
        const [activeDuration, setActiveDuration] = useState<number>(widgetData?.duration ?? 24);
        const [isRefreshed, setIsRefreshed] = React.useState(false);
        const [options, setOptions] = useState<any>({});

        const unitSuffixMap = useRef<Record<string, string>>({});

        React.useEffect(() => {
            if (!isMount) {
                setIsRefreshed(false);
                if (!isRefreshed)
                    payloadChangeHandler?.({
                        payload: channelIds,
                        activeDuration: getTimeInFromTo(activeDuration, 'h'),
                    });
            }
        }, [activeDuration]);

        React.useEffect(() => {
            if (!isMount && channelIds && !isLoading && !isFetching) {
                setIsRefreshed(false);
                if (!isRefreshed)
                    handlerLegendsPayload?.({
                        payload: channelIds,
                        activeDuration: getTimeInFromTo(activeDuration, 'h'),
                    });
            }
        }, [channelIds]);

        React.useEffect(() => {
            const unitList = Object.keys(unitsResponse?.data ?? {});
            if (unitList.length) {
                setUnit(unitList[0]);
            }
        }, [unitsResponse]);

        React.useEffect(() => {
            if (selectedModelCategory) {
                void getLoadTrendConfiguration({ modelCategory: selectedModelCategory });
            }
        }, [getLoadTrendConfiguration, selectedModelCategory]);
        const getDisplayName = (item: any): any =>
            selectedLoads?.find(
                (subItem: any) =>
                    subItem?.deviceId === item?.deviceId &&
                    configResponse?.data?.[subItem?.deviceTypeId]?.[`${subItem?.loadId}_${unit}`] === item?.channelId
            );

        // Finds the typical reporting cadence for this series so an abnormally large gap
        // (a period where no data was reported at all) can be told apart from normal
        // sampling jitter. The median is too easily skewed by dense clusters of points,
        // so we use a high percentile (95th) of the observed gaps as the "typical max"
        // interval, then apply a generous multiplier - real outages are orders of
        // magnitude larger than jitter, so this avoids flagging jitter as a gap.
        const getGapBreakThreshold = (points: Array<[number, number]>): number => {
            const diffs: number[] = [];
            for (let i = 1; i < points.length; i++) {
                const diff = points[i][0] - points[i - 1][0];
                if (diff > 0) diffs.push(diff);
            }
            if (!diffs.length) return Infinity;
            diffs.sort((a, b) => a - b);
            const typicalMaxInterval = diffs[Math.min(Math.floor(diffs.length * 0.95), diffs.length - 1)];
            return typicalMaxInterval > 0 ? typicalMaxInterval * 10 : Infinity;
        };

        // ECharts only breaks a line/area at an explicit `null` value (see `connectNulls`).
        // Since a missing time period simply has no records at all (not a null-valued one),
        // we insert a synthetic null point right after the last known point of each gap so
        // ECharts stops connecting/filling across periods where no data was reported.
        const insertGapBreaks = (rawPoints: any[]): any[] => {
            if (!rawPoints?.length) return rawPoints;
            const points = rawPoints
                .map((point: any): [number, any] => [Number(point?.[0]), point?.[1]])
                .filter((point) => !Number.isNaN(point[0]))
                .sort((a, b) => a[0] - b[0]);
            const threshold = getGapBreakThreshold(points);
            const result: any[] = [];
            points.forEach((point, index) => {
                result.push(point);
                const next = points[index + 1];
                if (next && next[0] - point[0] > threshold) {
                    result.push([point[0] + 1, null]);
                }
            });
            return result;
        };

        const getSeriesData = (data: any): any[] =>
            data?.map((trend: any, index: number) => {
                const seletedData = getDisplayName(trend);
                const seriesId = `${trend?.deviceId}-${trend?.channelId}`;
                const suffix = trend?.unit ? ` ${trend?.unit}` : '';
                unitSuffixMap.current[seriesId] = suffix;
                const color = loadColors[index];
                return {
                    id: seriesId,
                    name: seletedData?.name ?? '',
                    data: insertGapBreaks(
                        trend?.records?.map((point: any): any => [point?.x + timezoneOffset * 60000, point?.y]) ?? []
                    ),
                    type: widgetData?.type === 'area' ? 'line' : widgetData?.type ?? 'line',
                    connectNulls: false,
                    ...(widgetData?.type === 'area' && {
                        areaStyle: { opacity: 0.8 },
                    }),
                    lineStyle: { color },
                    itemStyle: { color },
                    showSymbol: false,
                    emphasis: { focus: 'series' },
                };
            });

        // Rounds a rough step (max / targetSplits) up to a "nice" number (1, 2, 5, or 10 x 10^n)
        // so axis ticks land on readable values instead of odd fractions.
        const getNiceStep = (roughStep: number): number => {
            if (!roughStep || roughStep <= 0) return 1;
            const exponent = Math.floor(Math.log10(roughStep));
            const magnitude = 10 ** exponent;
            const fraction = roughStep / magnitude;
            let niceFraction = 10;
            if (fraction <= 1) niceFraction = 1;
            else if (fraction <= 2) niceFraction = 2;
            else if (fraction <= 5) niceFraction = 5;
            return niceFraction * magnitude;
        };

        // Instead of stopping the axis flush at the tightly-fit "nice" max (e.g. 100 when the
        // highest value is 90), extend it by one more step so the topmost data point/threshold
        // isn't pinned to the very top of the chart (e.g. 90 -> 120 with a step of 20).
        const getYAxisMax = (maxValue: number, targetSplits = 5): number | undefined => {
            if (!maxValue || maxValue <= 0) return undefined;
            const step = getNiceStep(maxValue / targetSplits);
            const niceMax = Math.ceil(maxValue / step) * step;
            return niceMax + step;
        };

        const getYAxis = (maxValue?: number): any => {
            const resolvedMax = getYAxisMax(maxValue ?? 0);
            return {
                type: 'value',
                name: '',
                ...(resolvedMax !== undefined ? { max: resolvedMax } : {}),
                axisLabel: {
                    formatter: (value: number): string => `${pointConversion(value)}`,
                },
                splitLine: {
                    lineStyle: { type: 'solid' },
                },
            };
        };

        const addPoints = (message: any): void => {
            const instance = chartRef?.current?.getEchartsInstance();
            if (!instance) return;
            const currentOption = instance.getOption();
            const seriesArr = currentOption?.series as any[];
            if (!seriesArr) return;
            const seriesId = `${message?.deviceId}-${message?.channelId}`;
            const seriesIndex = seriesArr.findIndex((s: any) => s.id === seriesId);
            if (seriesIndex === -1) return;
            const newData = [
                ...(seriesArr[seriesIndex].data || []),
                [message.timestamp + timezoneOffset * 60000, +message.value],
            ];
            seriesArr[seriesIndex] = { ...seriesArr[seriesIndex], data: newData };
            if (message?.unit) {
                unitSuffixMap.current[seriesId] = ` ${message?.unit}`;
            }
            instance.setOption({ series: seriesArr });
            instance.hideLoading();
        };

        React.useImperativeHandle(
            ref,
            () => ({
                addPoints: (message: any): void => addPoints(message),
            }),
            [widgetData, timezoneOffset]
        );

        const setChannelIdsHandler = (loadConfig: any): void => {
            if (!configResponse?.data) return;
            const payload: any = [];
            const selectedUnit = unit || Object.keys(unitsResponse?.data ?? {})?.[0];
            loadConfig?.forEach((item: any) => {
                const channelId = configResponse?.data?.[item?.deviceTypeId]?.[`${item?.loadId}_${selectedUnit}`];
                if (channelId) {
                    const index = payload?.findIndex((subItem: any) => subItem?.deviceId === item?.deviceId);
                    if (index === -1) {
                        payload.push({
                            deviceId: item?.deviceId,
                            channelId: [channelId],
                        });
                    } else {
                        payload[index].channelId?.push(channelId);
                    }
                }
            });
            setChannelIds(payload);
        };

        React.useEffect(() => {
            if (unit && configResponse?.data && selectedLoads?.length) {
                setChannelIdsHandler(selectedLoads ?? []);
            }
        }, [configResponse?.data, unit]);

        const initialApiCall = async (): Promise<void> => {
            if (widgetData?.secondary && widgetData?.secondary?.length) {
                const seletectedLegend = widgetData?.secondary?.[0];
                setChannelIdsHandler(seletectedLegend?.loadConfig);
                setSelectedLoads(seletectedLegend?.loadConfig ?? []);
                await getLoadTrendConfiguration(
                    {
                        modelCategory: seletectedLegend?.modelCategory,
                    },
                    true
                );
                await getLoadTrendUnits(getDeviceType(seletectedLegend?.loadConfig), true);
            }
        };

        React.useEffect(() => {
            if (widgetData?.secondary) {
                setIsRefreshed(true);
                dispatch(
                    setLoadGroupTrendsLegendData({
                        id: widgetData?.widgetName,
                        value: {
                            legendData: widgetData?.secondary?.slice(0, widgetData?.maxCount ?? 10),
                            selectedId: widgetData?.secondary?.[0]?.id ?? '',
                        },
                    })
                );
                initialApiCall() as any;
            }
        }, [JSON.stringify(widgetData?.secondary), widgetData?.maxCount]);

        const getFilter = (): JSX.Element => (
            <Box display={'flex'} alignItems={'center'} position={'absolute'} right={0}>
                <FormControl sx={{ minWidth: 80 }}>
                    <Select
                        disabled={mode !== 'view'}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="Age"
                        variant="standard"
                        disableUnderline
                        defaultValue={widgetData?.duration ?? 24}
                        onChange={(e: any): void => setActiveDuration(e?.target?.value)}
                    >
                        <MenuItem value={24}>
                            <CustomTransComponent translationKey={'MULTI_TRENDS:TWENTY_FOUR_HOURS'} />
                        </MenuItem>
                        <MenuItem value={360}>
                            <CustomTransComponent translationKey={'MULTI_TRENDS:FIFTEEN_DAYS'} />
                        </MenuItem>
                        <MenuItem value={744}>
                            <CustomTransComponent translationKey={'MULTI_TRENDS:THIRTY_ONE_DAYS'} />
                        </MenuItem>
                    </Select>
                </FormControl>
                {(canDownloadLoadsGroupsTrends || canDownloadLoadsGroupsTrendsExpired) && (
                    <Button
                        disabled={mode !== 'view'}
                        startIcon={
                            <Download
                                sx={{
                                    color: mode !== 'view' ? '#b2b2b8' : '#727e84',
                                }}
                            />
                        }
                        onClick={(): void =>
                            downloadHandler?.({ channelIds, activeDuration: getTimeInFromTo(activeDuration, 'h') })
                        }
                        className="download-icon"
                    />
                )}
            </Box>
        );

        React.useEffect(() => {
            const instance = chartRef?.current?.getEchartsInstance();
            const data: any = JSON.parse(JSON.stringify(widgetData?.loadGroupTrends ?? []));
            if (
                data?.length &&
                data?.some((item: any) => Boolean(item?.records?.length)) &&
                trendsLegendData?.legendData?.length
            ) {
                const seriesData = getSeriesData(data);
                const maxDataValue = data?.reduce((max: number, trend: any) => {
                    const trendMax = trend?.records?.reduce(
                        (recordMax: number, record: any) => Math.max(recordMax, Number(record?.y) || 0),
                        0
                    );
                    return Math.max(max, trendMax ?? 0);
                }, 0);
                const formatTooltipTime = (value: number | string): string =>
                    moment.utc(value).format('dddd, DD MMM, HH:mm:ss');
                const newOption: any = {
                    useUTC: true,
                    grid: {
                        left: 60,
                        right: 30,
                        top: 20,
                        bottom: 50,
                        containLabel: false,
                    },
                    xAxis: {
                        type: 'time',
                        axisLabel: {
                            // Data x-values are pre-shifted into the target timezone, so format with
                            // UTC placeholders. ECharts auto-selects the level below based on zoom.
                            formatter: {
                                year: '{yyyy}',
                                month: '{dd} {MMM}',
                                day: '{dd} {MMM}',
                                hour: '{HH}:{mm}',
                                minute: '{HH}:{mm}',
                                second: '{HH}:{mm}:{ss}',
                            },
                        },
                    },
                    yAxis: getYAxis(maxDataValue),
                    series: seriesData,
                    tooltip: {
                        trigger: 'axis',
                        confine: true,
                        appendToBody: false,
                        backgroundColor: '#fff',
                        borderWidth: 0,
                        padding: 0,
                        extraCssText: 'box-shadow: 0 2px 10px rgba(0,0,0,0.15); border-radius: 8px;',
                        formatter: (params: any): string => {
                            if (!params?.length) return '';
                            const header = formatTooltipTime(params[0].value[0]);
                            const rows = params
                                ?.map((p: any) => {
                                    const suffix = unitSuffixMap.current[p.seriesId] ?? '';
                                    return `<div style="display:flex;align-items:center;gap:4px;padding:1px 0;">
                                        <span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${
                                            p.color
                                        };flex-shrink:0;"></span>
                                        <span style="color:#333;">${p.seriesName}:</span>
                                        <span style="font-weight:700;color:#333;">${pointConversion(
                                            p.value[1]
                                        )}${suffix}</span>
                                    </div>`;
                                })
                                .join('');
                            return `<div style="font-size:12px;font-family:inherit;padding:8px 12px;min-width:120px;">
                                <div style="color:#333;padding-bottom:4px;">${header}</div>
                                ${rows}
                            </div>`;
                        },
                    },
                    legend: { show: false },
                    dataZoom: [
                        {
                            type: 'inside',
                            xAxisIndex: 0,
                            filterMode: 'none',
                            zoomOnMouseWheel: true,
                            moveOnMouseWheel: false,
                            moveOnMouseMove: true,
                            throttle: 50,
                        },
                    ],
                };
                setOptions(newOption);
                instance?.hideLoading();
            } else {
                setOptions((prev: any) => ({ ...prev, series: [] }));
            }
        }, [widgetData?.loadGroupTrends, chartHeight, trendsLegendData, timezone]);

        const changeHandler = async (e: any, id: number): Promise<void> => {
            const newLegendData = JSON.parse(JSON.stringify(trendsLegendData?.legendData ?? []));
            const seletectedLegend = newLegendData?.find((item: any) => item?.id === id);
            setSelectedLoads(seletectedLegend?.loadConfig ?? []);
            dispatch(
                setLoadGroupTrendsLegendData({
                    id: widgetData?.widgetName,
                    value: {
                        legendData: newLegendData,
                        selectedId: id,
                    },
                })
            );
            await getLoadTrendConfiguration(
                {
                    modelCategory: seletectedLegend?.modelCategory,
                },
                true
            );
            await getLoadTrendUnits(getDeviceType(seletectedLegend?.loadConfig), true);
            const instance = chartRef?.current?.getEchartsInstance();
            if (instance) {
                instance.setOption({ series: [] }, true);
            }
        };

        const handleResizeComponent = (): void => {
            if (isFirstRender.current && mode === 'configured') {
                isFirstRender.current = false; // Skip the first trigger
                return;
            }
            const hightWidth = componentRef.current?.getElementsByClassName('d-flex')?.[0]?.getBoundingClientRect();
            setChartHeight(
                Math.floor((componentRef.current.getBoundingClientRect()?.height ?? 0) - (hightWidth?.height ?? 0) - 16)
            );
        };

        React.useEffect(() => {
            const observer = new ResizeObserver(handleResizeComponent);
            if (componentRef.current && trendsLegendData?.legendData?.length) {
                window.addEventListener('resize', handleResizeComponent);
                observer.observe(componentRef.current);
            }

            return () => {
                window.removeEventListener('resize', handleResizeComponent);
                observer.disconnect();
            };
        }, [trendsLegendData?.legendData?.length]);

        const unitChangeHandler = (e: any): void => {
            setUnit(e.target.value);

            const payload: any = [];
            selectedLoads.forEach((item: any) => {
                const channelId = configResponse?.data?.[item?.deviceTypeId]?.[`${item?.loadId}_${e.target.value}`];
                if (!channelId) return;
                const index = payload.findIndex((p: any) => p.deviceId === item.deviceId);

                if (index === -1) {
                    payload.push({ deviceId: item.deviceId, channelId: [channelId] });
                } else {
                    payload[index].channelId.push(channelId);
                }
            });

            setChannelIds(payload);

            payloadChangeHandler?.({
                payload,
                activeDuration: getTimeInFromTo(activeDuration, 'h'),
            });
        };
        return (
            <Paper sx={{ height: '100%' }}>
                <WidgetHeader mode={mode} widgetData={widgetData} headerAction={getFilter()} />
                {mode !== 'edit' && <Divider />}
                <Stack
                    sx={{
                        height: `calc(100% - 97px)`,
                        overflow: 'auto',
                        justifyContent: 'space-between',
                    }}
                    ref={componentRef}
                >
                    <Stack
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                        alignItems={'flex-end'}
                        p={2}
                        pr={5}
                    >
                        <Box mr={2}>
                            <FormControl variant="standard">
                                <Select
                                    value={unit}
                                    disabled={mode !== 'view'}
                                    onChange={unitChangeHandler}
                                    disableUnderline
                                    IconComponent={ArrowDropDown}
                                    sx={{
                                        minWidth: 100,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                mt: 1,
                                                borderRadius: 1,
                                                boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                                            },
                                        },
                                    }}
                                    renderValue={() => (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography fontWeight={500}>Units</Typography>
                                        </Box>
                                    )}
                                >
                                    {Object.keys(unitsResponse?.data ?? {}).map((item) => (
                                        <MenuItem
                                            key={item}
                                            value={item}
                                            sx={{
                                                fontSize: 14,
                                                '&.Mui-selected': {
                                                    backgroundColor: '#424E540D',
                                                },
                                            }}
                                        >
                                            {unitsResponse?.data[item]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                    <div
                        style={{
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%',
                            height: chartHeight ? `${chartHeight}px` : '450px',
                        }}
                    >
                        <ReactECharts
                            ref={chartRef}
                            option={options}
                            notMerge={true}
                            style={{ height: '100%', width: '100%' }}
                        />
                    </div>
                    <MultiColumnList
                        isDisabled={mode !== 'view'}
                        isLoading={false}
                        legendData={trendsLegendData?.legendData ?? []}
                        selectedId={trendsLegendData?.selectedId}
                        changeHandler={changeHandler}
                        maxItemsPerColumn={5}
                        type="radio"
                        targetKey="id"
                        multiDevice={widgetData?.multiDevice}
                        isTotalCount={true}
                    />
                </Stack>
                {(canViewLoadsGroupsTrends || canViewLoadsGroupsTrendsExpired) && (
                    <WidgetFooter mode={mode} widgetData={widgetData} />
                )}
            </Paper>
        );
    }
);
export default Component;
